import { useRef, useCallback, useEffect } from "react";

export interface SoundSettings {
  volume: number;
  pitch: number;
  decay: number;
}

export const SOUNDS = [
  { id: "kick", name: "Kick", key: "1" },
  { id: "snare", name: "Snare", key: "2" },
  { id: "clap", name: "Clap", key: "3" },
  { id: "rimshot", name: "Rim", key: "4" },
  { id: "closedhat", name: "CH", key: "q" },
  { id: "openhat", name: "OH", key: "w" },
  { id: "cymbal", name: "Cymbal", key: "e" },
  { id: "cowbell", name: "Cowbell", key: "r" },
  { id: "tom1", name: "Tom H", key: "a" },
  { id: "tom2", name: "Tom M", key: "s" },
  { id: "tom3", name: "Tom L", key: "d" },
  { id: "conga", name: "Conga", key: "f" },
  { id: "maracas", name: "Maracas", key: "z" },
  { id: "clave", name: "Clave", key: "x" },
  { id: "shake", name: "Shake", key: "c" },
  { id: "noise", name: "Noise", key: "v" },
] as const;

export type SoundId = (typeof SOUNDS)[number]["id"];

const MAX_VOLUME = 1.1;

function createDistortion(ctx: AudioContext, amount: number): Float32Array {
  const k = amount * 100;
  const samples = 44100;
  const curve = new Float32Array(samples);
  const deg = Math.PI / 180;
  for (let i = 0; i < samples; i++) {
    const x = (i * 2) / samples - 1;
    curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
  }
  return curve;
}

export function useDrumSynth() {
  const ctxRef = useRef<AudioContext | null>(null);
  const distortionRef = useRef(false);
  const distortionNodeRef = useRef<WaveShaperNode | null>(null);

  const init = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    ctxRef.current = ctx;

    // Create distortion node
    distortionNodeRef.current = ctx.createWaveShaper();
    distortionNodeRef.current.curve = createDistortion(ctx, 0.4);

    return ctx;
  }, []);

  const setDistortion = useCallback((enabled: boolean) => {
    distortionRef.current = enabled;
  }, []);

  const playSound = useCallback((type: SoundId, settings: SoundSettings, velocity = 127) => {
    const ctx = init();
    if (ctx.state === "suspended") ctx.resume();

    const now = ctx.currentTime;
    const pitchMult = Math.pow(2, settings.pitch / 12);
    const decayTime = 0.1 + settings.decay * 0.9;

    const masterGain = ctx.createGain();
    const velocityScale = velocity / 127;
    masterGain.gain.value = settings.volume * velocityScale;

    if (distortionRef.current && distortionNodeRef.current) {
      distortionNodeRef.current.connect(ctx.destination);
      masterGain.connect(distortionNodeRef.current);
    } else {
      masterGain.connect(ctx.destination);
    }

    const sounds: Record<SoundId, () => void> = {
      kick: () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(masterGain);
        osc.frequency.setValueAtTime(150 * pitchMult, now);
        osc.frequency.exponentialRampToValueAtTime(30, now + 0.12 * decayTime);
        gain.gain.setValueAtTime(1, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4 * decayTime);
        osc.start(now);
        osc.stop(now + 0.4 * decayTime);
      },
      snare: () => {
        const osc = ctx.createOscillator();
        const oscGain = ctx.createGain();
        osc.connect(oscGain);
        oscGain.connect(masterGain);
        osc.frequency.setValueAtTime(180 * pitchMult, now);
        oscGain.gain.setValueAtTime(0.6, now);
        oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
        osc.start(now);
        osc.stop(now + 0.1);

        const noise = ctx.createBufferSource();
        const noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * 0.2, ctx.sampleRate);
        const data = noiseBuffer.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
        noise.buffer = noiseBuffer;
        const noiseGain = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        filter.type = "highpass";
        filter.frequency.value = 1500 * pitchMult;
        noise.connect(filter);
        filter.connect(noiseGain);
        noiseGain.connect(masterGain);
        noiseGain.gain.setValueAtTime(0.5, now);
        noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15 * decayTime);
        noise.start(now);
      },
      clap: () => {
        for (let i = 0; i < 4; i++) {
          const noise = ctx.createBufferSource();
          const buf = ctx.createBuffer(1, ctx.sampleRate * 0.02, ctx.sampleRate);
          const d = buf.getChannelData(0);
          for (let j = 0; j < d.length; j++) d[j] = Math.random() * 2 - 1;
          noise.buffer = buf;
          const filter = ctx.createBiquadFilter();
          filter.type = "bandpass";
          filter.frequency.value = 2500 * pitchMult;
          const gain = ctx.createGain();
          noise.connect(filter);
          filter.connect(gain);
          gain.connect(masterGain);
          const t = now + i * 0.015;
          gain.gain.setValueAtTime(0.4, t);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.08 * decayTime);
          noise.start(t);
        }
      },
      rimshot: () => {
        const osc = ctx.createOscillator();
        osc.type = "triangle";
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(masterGain);
        osc.frequency.setValueAtTime(820 * pitchMult, now);
        gain.gain.setValueAtTime(0.6, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
        osc.start(now);
        osc.stop(now + 0.03);
      },
      closedhat: () => {
        const noise = ctx.createBufferSource();
        const buf = ctx.createBuffer(1, ctx.sampleRate * 0.08, ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
        noise.buffer = buf;
        const filter = ctx.createBiquadFilter();
        filter.type = "highpass";
        filter.frequency.value = 6000 * pitchMult;
        const gain = ctx.createGain();
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(masterGain);
        gain.gain.setValueAtTime(0.5, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.06 * decayTime);
        noise.start(now);
      },
      openhat: () => {
        const noise = ctx.createBufferSource();
        const buf = ctx.createBuffer(1, ctx.sampleRate * 0.3, ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
        noise.buffer = buf;
        const filter = ctx.createBiquadFilter();
        filter.type = "highpass";
        filter.frequency.value = 5000 * pitchMult;
        const gain = ctx.createGain();
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(masterGain);
        gain.gain.setValueAtTime(0.6, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25 * decayTime);
        noise.start(now);
      },
      cymbal: () => {
        const noise = ctx.createBufferSource();
        const buf = ctx.createBuffer(1, ctx.sampleRate * 0.6, ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
        noise.buffer = buf;
        const filter = ctx.createBiquadFilter();
        filter.type = "highpass";
        filter.frequency.value = 5000 * pitchMult;
        const gain = ctx.createGain();
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(masterGain);
        gain.gain.setValueAtTime(0.4, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5 * decayTime);
        noise.start(now);
      },
      cowbell: () => {
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain = ctx.createGain();
        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(masterGain);
        osc1.frequency.value = 560 * pitchMult;
        osc2.frequency.value = 845 * pitchMult;
        gain.gain.setValueAtTime(0.5, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3 * decayTime);
        osc1.start(now);
        osc2.start(now);
        osc1.stop(now + 0.3 * decayTime);
        osc2.stop(now + 0.3 * decayTime);
      },
      tom1: () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(masterGain);
        osc.frequency.setValueAtTime(280 * pitchMult, now);
        osc.frequency.exponentialRampToValueAtTime(140, now + 0.12);
        gain.gain.setValueAtTime(0.7, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25 * decayTime);
        osc.start(now);
        osc.stop(now + 0.25 * decayTime);
      },
      tom2: () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(masterGain);
        osc.frequency.setValueAtTime(200 * pitchMult, now);
        osc.frequency.exponentialRampToValueAtTime(100, now + 0.12);
        gain.gain.setValueAtTime(0.7, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3 * decayTime);
        osc.start(now);
        osc.stop(now + 0.3 * decayTime);
      },
      tom3: () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(masterGain);
        osc.frequency.setValueAtTime(140 * pitchMult, now);
        osc.frequency.exponentialRampToValueAtTime(70, now + 0.15);
        gain.gain.setValueAtTime(0.8, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35 * decayTime);
        osc.start(now);
        osc.stop(now + 0.35 * decayTime);
      },
      conga: () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(masterGain);
        osc.frequency.setValueAtTime(350 * pitchMult, now);
        osc.frequency.exponentialRampToValueAtTime(180, now + 0.05);
        gain.gain.setValueAtTime(0.7, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15 * decayTime);
        osc.start(now);
        osc.stop(now + 0.15 * decayTime);
      },
      maracas: () => {
        const noise = ctx.createBufferSource();
        const buf = ctx.createBuffer(1, ctx.sampleRate * 0.06, ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
        noise.buffer = buf;
        const filter = ctx.createBiquadFilter();
        filter.type = "highpass";
        filter.frequency.value = 6000 * pitchMult;
        const gain = ctx.createGain();
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(masterGain);
        gain.gain.setValueAtTime(0.35, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
        noise.start(now);
      },
      clave: () => {
        const osc = ctx.createOscillator();
        osc.type = "triangle";
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(masterGain);
        osc.frequency.setValueAtTime(2500 * pitchMult, now);
        gain.gain.setValueAtTime(0.5, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
        osc.start(now);
        osc.stop(now + 0.04);
      },
      shake: () => {
        for (let i = 0; i < 8; i++) {
          const noise = ctx.createBufferSource();
          const buf = ctx.createBuffer(1, ctx.sampleRate * 0.012, ctx.sampleRate);
          const d = buf.getChannelData(0);
          for (let j = 0; j < d.length; j++) d[j] = Math.random() * 2 - 1;
          noise.buffer = buf;
          const filter = ctx.createBiquadFilter();
          filter.type = "highpass";
          filter.frequency.value = 6000 * pitchMult;
          const gain = ctx.createGain();
          noise.connect(filter);
          filter.connect(gain);
          gain.connect(masterGain);
          const t = now + i * 0.018;
          gain.gain.setValueAtTime(0.15, t);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.015);
          noise.start(t);
        }
      },
      noise: () => {
        const noise = ctx.createBufferSource();
        const buf = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
        noise.buffer = buf;
        const gain = ctx.createGain();
        noise.connect(gain);
        gain.connect(masterGain);
        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4 * decayTime);
        noise.start(now);
      },
    };

    sounds[type]();
  }, [init]);

  // Resume audio context when page becomes visible (after phone lock/unlock)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && ctxRef.current?.state === "suspended") {
        ctxRef.current.resume();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      ctxRef.current?.close();
    };
  }, []);

  return { playSound, setDistortion };
}

export function getDefaultSettings(): Record<SoundId, SoundSettings> {
  const settings: Record<string, SoundSettings> = {};
  SOUNDS.forEach((s) => {
    settings[s.id] = { volume: 0.88, pitch: 0, decay: 0.5 };
  });
  return settings as Record<SoundId, SoundSettings>;
}
